import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { Warning, Trash, ArrowsClockwise } from '@phosphor-icons/react';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const CONFIRM_PHRASE = 'DELETE-ALL-DATA';

const COLLECTION_LABELS = {
  sales_applications: 'Applications',
  items: 'Items',
  sellers: 'Sellers',
  bookings: 'Bookings',
  payouts: 'Payouts',
  tasks: 'Tasks',
  sms_messages: 'SMS Messages',
  emails: 'Emails',
  activity_logs: 'Activity Logs',
  agreements: 'Sales Agreements',
  buyer_enquiries: 'Buyer Enquiries',
  buyers: 'Buyers',
  sms_logs: 'SMS Logs',
  notes: 'Notes',
};

export default function Settings() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';

  const [counts, setCounts] = useState(null);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [purging, setPurging] = useState(false);
  const [verifyCfg, setVerifyCfg] = useState(null);
  const [tempBusy, setTempBusy] = useState(false);
  const [digestPreview, setDigestPreview] = useState(null);
  const [digestSending, setDigestSending] = useState(false);

  const fetchDigestPreview = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/digest/preview`, { withCredentials: true });
      setDigestPreview(data);
    } catch (err) {
      setDigestPreview(null);
    }
  }, []);

  const sendDigestNow = async () => {
    setDigestSending(true);
    try {
      await axios.post(`${API_URL}/api/digest/send`, {}, { withCredentials: true });
      toast.success('Digest SMS sent to your phone');
      fetchDigestPreview();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send digest');
    } finally {
      setDigestSending(false);
    }
  };

  const fetchVerifyCfg = useCallback(async () => {
    try {
      const { data } = await axios.get(
        `${API_URL}/api/admin/maintenance/verification-config`,
        { withCredentials: true }
      );
      setVerifyCfg(data);
    } catch (err) {
      toast.error('Failed to load verification config');
    }
  }, []);

  const handleTempAdmin = async (action) => {
    setTempBusy(true);
    try {
      if (action === 'create') {
        await axios.post(`${API_URL}/api/admin/maintenance/temp-admin`, {}, { withCredentials: true });
        toast.success('Temporary verification admin created (48h expiry)');
      } else {
        await axios.delete(`${API_URL}/api/admin/maintenance/temp-admin`, { withCredentials: true });
        toast.success('Temporary verification admin removed');
      }
      fetchVerifyCfg();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Action failed');
    } finally {
      setTempBusy(false);
    }
  };

  const fetchCounts = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.post(
        `${API_URL}/api/admin/maintenance/purge-all-data`,
        {},
        { withCredentials: true }
      );
      setCounts(data.records_that_would_be_deleted || {});
      setTotal(data.total || 0);
    } catch (err) {
      toast.error('Failed to load data counts');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isAdmin) { fetchCounts(); fetchVerifyCfg(); fetchDigestPreview(); }
  }, [isAdmin, fetchCounts, fetchVerifyCfg, fetchDigestPreview]);

  const handlePurge = async () => {
    if (confirmText !== CONFIRM_PHRASE) return;
    setPurging(true);
    try {
      const { data } = await axios.post(
        `${API_URL}/api/admin/maintenance/purge-all-data?confirm=${CONFIRM_PHRASE}`,
        {},
        { withCredentials: true }
      );
      toast.success(`Purge complete — ${data.total_deleted} records permanently deleted`);
      setDialogOpen(false);
      setConfirmText('');
      fetchCounts();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Purge failed');
    } finally {
      setPurging(false);
    }
  };

  return (
      <div className="p-6 md:p-8 max-w-4xl">
        <h1 className="text-2xl font-bold text-gray-900 mb-1" data-testid="settings-title">Settings</h1>
        <p className="text-sm text-gray-500 mb-8">System administration and maintenance</p>

        {isAdmin && (
          <div className="border border-gray-200 rounded-lg bg-white overflow-hidden mb-8" data-testid="verification-panel">
            <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
              <h2 className="text-base font-bold text-gray-900">Production Verification</h2>
            </div>
            <div className="p-6">
              {verifyCfg ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-2 text-sm mb-5" data-testid="verification-config">
                  <div className="flex justify-between"><span className="text-gray-500">SMS provider</span><span className="font-medium text-gray-900">MobileMessage</span></div>
                  <div className="flex justify-between"><span className="text-gray-500">SMS mode</span><span className={`font-bold ${verifyCfg.sms_mode === 'live' ? 'text-green-700' : 'text-amber-600'}`}>{verifyCfg.sms_mode}</span></div>
                  <div className="flex justify-between"><span className="text-gray-500">Sender ID</span><span className="font-medium text-gray-900">{verifyCfg.sms_sender_id || '—'}</span></div>
                  <div className="flex justify-between"><span className="text-gray-500">Test SMS number</span><span className="font-medium text-gray-900">{verifyCfg.test_sms_number_set ? `set (${verifyCfg.test_sms_number_masked})` : 'NOT SET'}</span></div>
                  <div className="flex justify-between"><span className="text-gray-500">Google review URL</span><span className="font-medium text-gray-900">{verifyCfg.google_review_url_set ? `set (${verifyCfg.google_review_domain})` : 'NOT SET'}</span></div>
                  <div className="flex justify-between"><span className="text-gray-500">Temp verification admin</span><span className="font-medium text-gray-900">{verifyCfg.temp_admin?.email ? (verifyCfg.temp_admin.disabled ? 'disabled' : `active until ${new Date(verifyCfg.temp_admin.expires_at).toLocaleString()}`) : 'not created'}</span></div>
                </div>
              ) : (
                <p className="text-sm text-gray-400 mb-5">Loading…</p>
              )}
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={() => handleTempAdmin('create')}
                  disabled={tempBusy}
                  data-testid="create-temp-admin-button"
                >
                  Create Verification Admin (48h)
                </Button>
                <Button
                  variant="outline"
                  className="text-red-600 border-red-200 hover:bg-red-50"
                  onClick={() => handleTempAdmin('remove')}
                  disabled={tempBusy || !verifyCfg?.temp_admin?.email}
                  data-testid="remove-temp-admin-button"
                >
                  Remove Verification Admin
                </Button>
              </div>
              <p className="text-xs text-gray-400 mt-3">
                The verification admin's password is read from the protected TEMP_ADMIN_PASSWORD environment
                variable — it is never shown here or returned by the API.
              </p>
            </div>
          </div>
        )}

        {isAdmin && (
          <div className="border border-gray-200 rounded-lg bg-white overflow-hidden mb-8" data-testid="digest-panel">
            <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
              <h2 className="text-base font-bold text-gray-800">Weekly Digest SMS</h2>
              <p className="text-xs text-gray-500 mt-0.5">Sent to your phone every Monday from 8am Perth time — a wrap of sales, applications and anything needing attention.</p>
            </div>
            <div className="p-6">
              {digestPreview ? (
                <>
                  <p className="text-sm text-gray-700 bg-gray-50 border border-gray-200 rounded-lg p-3 mb-3" data-testid="digest-preview-text">{digestPreview.message}</p>
                  <p className="text-xs text-gray-400 mb-1">
                    {digestPreview.last_sent_at ? `Last sent: ${new Date(digestPreview.last_sent_at).toLocaleString('en-AU')}` : 'Never sent yet'}
                  </p>
                  {!digestPreview.notify_number_configured && (
                    <p className="text-xs font-semibold text-red-600 mb-4" data-testid="digest-notify-warning">
                      NOTIFY_SMS_NUMBER is not configured — the digest cannot send until it is set.
                    </p>
                  )}
                  <div className="mb-3" />
                </>
              ) : (
                <p className="text-sm text-gray-400 mb-4">Loading preview…</p>
              )}
              <Button size="sm" onClick={sendDigestNow} disabled={digestSending}
                className="bg-[#C6E76C] hover:bg-[#b5d65f] text-[#1E2117] font-semibold" data-testid="send-digest-button">
                {digestSending ? 'Sending…' : 'Send Test Digest Now'}
              </Button>
            </div>
          </div>
        )}

        {!isAdmin ? (
          <div className="bg-white border border-gray-200 rounded-lg p-8 text-center" data-testid="settings-no-access">
            <p className="text-gray-600">Administrator access is required to view system settings.</p>
          </div>
        ) : (
          <div className="border-2 border-red-300 rounded-lg bg-red-50/50 overflow-hidden" data-testid="danger-zone">
            <div className="px-6 py-4 bg-red-100/60 border-b border-red-200 flex items-center gap-2">
              <Warning size={20} weight="fill" className="text-red-600" />
              <h2 className="text-base font-bold text-red-800">Danger Zone</h2>
            </div>

            <div className="p-6">
              <h3 className="text-sm font-semibold text-gray-900 mb-1">Purge All Test Data</h3>
              <p className="text-sm text-gray-600 mb-4">
                Permanently deletes every business record below — applications, items, sellers, bookings,
                payouts, tasks, messages and logs. Staff logins and accounts are <strong>not</strong> affected.
                This cannot be undone.
              </p>

              <div className="bg-white border border-gray-200 rounded-md p-4 mb-4">
                <div className="flex items-center justify-between mb-3">
                  <p className="text-xs font-bold tracking-wider text-gray-500 uppercase">
                    Records that will be deleted
                  </p>
                  <button
                    onClick={fetchCounts}
                    disabled={loading}
                    className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-800"
                    data-testid="refresh-counts-button"
                  >
                    <ArrowsClockwise size={14} className={loading ? 'animate-spin' : ''} />
                    Refresh
                  </button>
                </div>
                {counts === null ? (
                  <p className="text-sm text-gray-400">Loading…</p>
                ) : (
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-6 gap-y-1.5">
                    {Object.entries(counts)
                      .filter(([, v]) => v > 0)
                      .map(([coll, count]) => (
                        <div key={coll} className="flex justify-between text-sm">
                          <span className="text-gray-600">{COLLECTION_LABELS[coll] || coll}</span>
                          <span className="font-semibold text-gray-900">{count}</span>
                        </div>
                      ))}
                    {Object.values(counts).every((v) => v === 0) && (
                      <p className="text-sm text-gray-500 col-span-3">No business records — database is clean.</p>
                    )}
                  </div>
                )}
                {total > 0 && (
                  <p className="mt-3 pt-3 border-t border-gray-100 text-sm font-bold text-red-700" data-testid="total-records-count">
                    Total: {total} records
                  </p>
                )}
              </div>

              <Button
                variant="destructive"
                onClick={() => setDialogOpen(true)}
                disabled={total === 0}
                data-testid="purge-all-data-button"
              >
                <Trash size={16} className="mr-2" />
                Purge All Test Data
              </Button>
              {total === 0 && counts !== null && (
                <p className="text-xs text-gray-400 mt-2">Nothing to purge.</p>
              )}
            </div>
          </div>
        )}

        <AlertDialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) setConfirmText(''); }}>
          <AlertDialogContent data-testid="purge-confirm-dialog">
            <AlertDialogHeader>
              <AlertDialogTitle className="text-red-700">Permanently delete all data?</AlertDialogTitle>
              <AlertDialogDescription>
                This will permanently delete <strong>{total} records</strong> across all business
                collections. This action <strong>cannot be undone</strong>.
                <br /><br />
                Type <span className="font-mono font-bold text-red-700">{CONFIRM_PHRASE}</span> below to confirm.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <Input
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              placeholder={CONFIRM_PHRASE}
              className="font-mono"
              data-testid="purge-confirm-input"
            />
            <AlertDialogFooter>
              <Button
                variant="outline"
                onClick={() => { setDialogOpen(false); setConfirmText(''); }}
                data-testid="purge-cancel-button"
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={handlePurge}
                disabled={confirmText !== CONFIRM_PHRASE || purging}
                data-testid="purge-execute-button"
              >
                {purging ? 'Deleting…' : 'Delete Everything'}
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
  );
}
